import { useState } from "react";
import { Mic, MicOff, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useVoiceCapture, VoiceCaptureResult } from "@/hooks/useVoiceCapture";
import { VoiceTaskConfirmation } from "./VoiceTaskConfirmation";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { trackFeatureUsed } from "@/lib/analytics";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface VoiceTaskCaptureProps {
  contextJobId?: string;
  onTaskCreated?: () => void;
  variant?: "floating" | "inline";
}

export const VoiceTaskCapture = ({
  contextJobId,
  onTaskCreated,
  variant = "floating",
}: VoiceTaskCaptureProps) => {
  const {
    isRecording,
    isProcessing,
    recordingDuration,
    startRecording,
    stopRecording,
    processVoiceTask,
    cancelRecording,
  } = useVoiceCapture();

  const [captureResult, setCaptureResult] = useState<VoiceCaptureResult | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleAutoStop = async () => {
    console.log("Auto-stopping due to 5 seconds of silence");
    const audioBlob = await stopRecording();
    
    try {
      const result = await processVoiceTask(audioBlob, contextJobId);
      setCaptureResult(result);
      setShowConfirmation(true);
    } catch (error: any) {
      console.error("Failed to process voice:", error);
      toast.error("Failed to process voice task", {
        description: error.message || "Please try again"
      });
    }
  };

  const handleMicClick = async () => {
    if (isRecording) {
      // Stop recording and process
      const audioBlob = await stopRecording();
      
      try {
        const result = await processVoiceTask(audioBlob, contextJobId);
        setCaptureResult(result);
        setShowConfirmation(true);
      } catch (error: any) {
        console.error("Failed to process voice:", error);
        toast.error("Failed to process voice task", {
          description: error.message || "Please try again"
        });
      }
    } else {
      // Start recording with auto-stop callback
      const success = await startRecording(handleAutoStop);
      if (!success) {
        // Error toast already shown in hook
      }
    }
  };

  const handleCancel = () => {
    if (isRecording) {
      cancelRecording();
    }
    setShowConfirmation(false);
    setCaptureResult(null);
  };

  const handleTaskCreated = () => {
    setShowConfirmation(false);
    setCaptureResult(null);
    trackFeatureUsed('voice_task_capture', { contextJobId });
    onTaskCreated?.();
  };

  if (showConfirmation && captureResult) {
    return (
      <VoiceTaskConfirmation
        transcript={captureResult.transcript}
        parsedFields={captureResult.parsedFields}
        logId={captureResult.logId}
        contextJobId={contextJobId}
        onConfirm={handleTaskCreated}
        onCancel={handleCancel}
      />
    );
  }

  const baseStyles = cn(
    "transition-all duration-300 shadow-lg",
    isRecording
      ? "bg-destructive hover:bg-destructive/90 text-destructive-foreground animate-pulse"
      : "bg-[#D6B77A] hover:bg-[#D6B77A]/90 text-[#1F2A44]"
  );

  const floatingStyles = "fixed bottom-24 right-4 z-40 rounded-full h-14 w-14";
  const inlineStyles = "rounded-full h-12 w-12";

  if (variant === "inline") {
    return (
      <div className="relative">
        <Button
          size="icon"
          onClick={handleMicClick}
          disabled={isProcessing}
          className={cn(baseStyles, inlineStyles)}
          aria-label={isRecording ? "Stop recording" : "Start voice task"}
        >
          {isProcessing ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : isRecording ? (
            <MicOff className="h-5 w-5" />
          ) : (
            <Mic className="h-5 w-5" />
          )}
        </Button>
        
        {isRecording && (
          <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-destructive text-destructive-foreground px-3 py-1 rounded-full text-xs font-mono font-semibold whitespace-nowrap">
            {formatDuration(recordingDuration)}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="fixed bottom-20 right-4 z-40 flex flex-col items-end gap-1">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              onClick={handleMicClick}
              disabled={isProcessing}
              className={cn(
                baseStyles,
                "h-12 w-12 rounded-full shadow-lg transition-all p-0"
              )}
              aria-label={isRecording ? "Stop recording" : "Start voice task"}
            >
              {isProcessing ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : isRecording ? (
                <MicOff className="h-5 w-5" />
              ) : (
                <Mic className="h-5 w-5" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="left" className="max-w-xs">
            <p className="font-semibold mb-1">Quick Task Entry (up to 30 sec)</p>
            <p className="text-xs text-muted-foreground">
              Tap to record a task in natural language. Say things like "Order 10 sheets of drywall from Home Depot for the Johnson job" or "Bring the miter saw to site tomorrow." The system extracts details automatically.
            </p>
            <p className="text-xs text-muted-foreground mt-2 font-medium">
              Recording automatically stops after 5 seconds of silence.
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* Recording indicator banner */}
      {isRecording && (
          <div className="fixed top-0 left-0 right-0 bg-destructive text-destructive-foreground py-2 px-4 z-50 flex items-center justify-center gap-2 shadow-lg">
            <div className="flex gap-1">
              <div className="w-1 h-4 bg-white animate-pulse" style={{ animationDelay: "0ms" }}></div>
              <div className="w-1 h-4 bg-white animate-pulse" style={{ animationDelay: "150ms" }}></div>
              <div className="w-1 h-4 bg-white animate-pulse" style={{ animationDelay: "300ms" }}></div>
            </div>
            <span className="font-semibold">Recording</span>
            <span className="font-mono">{formatDuration(recordingDuration)}</span>
            <Button
              size="sm"
              variant="secondary"
              onClick={handleCancel}
              className="ml-4"
            >
              Cancel
            </Button>
          </div>
        )}

        {/* Processing overlay */}
        {isProcessing && (
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center">
            <div className="bg-card p-6 rounded-lg shadow-xl flex flex-col items-center gap-4 max-w-sm mx-4">
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
              <div className="text-center">
                <p className="font-semibold text-lg">Processing your task...</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Transcribing audio and extracting details
                </p>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};
